import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, ArrowBack, GetApp } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#2d35a0';
const PRIMARY_LT = '#eef0ff';

const fmtV = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 28, width: 28, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const GSTR3BFormatReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate] = useState(fmt2Date(today));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fetched, setFetched] = useState(false);

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token, fromDate, toDate]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) return;
        if (new Date(fromDate) > new Date(toDate)) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetchData('GSTReportApi', `/GSTR3BNewDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            const result = res?.response_data || res || {};
            if (!result || (Array.isArray(result) && result.length === 0) || (!result.GSTR3B && !result.Summary)) {
                setData(null);
            } else {
                setData(result);
            }
            setFetched(true);
        } catch (err) {
            console.error(err);
            setData(null);
            setFetched(true);
        } finally { setLoading(false); }
    };

    // Helper to find rows by section
    const getSection = (sectionNo) => (data?.GSTR3B || []).filter(r => r.SectionNo === sectionNo);
    const getRow = (sectionNo, slNo) => (data?.GSTR3B || []).find(r => r.SectionNo === sectionNo && r.SlNo === slNo);
    const summary = data?.Summary || {};
    const salesRateWise = data?.SalesRateWise || [];
    const purchaseRateWise = data?.PurchaseRateWise || [];

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'GSTR3BFormatReport', 0);
        if (!data) return;
        const gstr3b = data.GSTR3B || [];
        const headers = ['Section', 'Sl No', 'Particulars', 'Taxable Value', 'IGST', 'CGST', 'SGST', 'CESS'];
        const rows = [];

        // Section 3.1
        rows.push(['', '', '3.1 - Tax on Outward and Reverse Charge Inward Supplies', '', '', '', '', '']);
        gstr3b.filter(r => r.SectionNo === '3.1').forEach(r => {
            rows.push([r.SectionNo, r.SlNo, r.Particulars, r.TaxableValue, r.IGST, r.CGST, r.SGST, r.CESS]);
        });

        // Section 4
        rows.push(['', '', '', '', '', '', '', '']);
        rows.push(['', '', '4 - Eligible ITC', '', '', '', '', '']);
        gstr3b.filter(r => r.SectionNo === '4').forEach(r => {
            rows.push([r.SectionNo, r.SlNo, r.Particulars, r.TaxableValue, r.IGST, r.CGST, r.SGST, r.CESS]);
        });

        // Section 5
        rows.push(['', '', '', '', '', '', '', '']);
        rows.push(['', '', '5 - Values of Exempt, Nil-Rated and Non-GST Inward Supplies', '', '', '', '', '']);
        gstr3b.filter(r => r.SectionNo === '5').forEach(r => {
            rows.push([r.SectionNo, r.SlNo, r.Particulars, r.TaxableValue, r.IGST, 0, 0, r.CESS]);
        });

        // Section 6
        rows.push(['', '', '', '', '', '', '', '']);
        rows.push(['', '', '6 - Payment of Tax', '', '', '', '', '']);
        gstr3b.filter(r => r.SectionNo === '6').forEach(r => {
            rows.push([r.SectionNo, r.SlNo, r.Particulars, r.TaxableValue, r.IGST, r.CGST, r.SGST, r.CESS]);
        });

        // Sales Rate Wise
        if (salesRateWise.length > 0) {
            rows.push(['', '', '', '', '', '', '', '']);
            rows.push(['', '', 'Sales - GST Rate Wise Breakup', '', '', '', '', '']);
            rows.push(['', '', 'GST Rate %', 'Taxable', 'IGST', 'CGST', 'SGST', '']);
            salesRateWise.forEach(r => {
                rows.push(['', '', `${r.GSTRate}%`, r.Taxable, r.IGST, r.CGST, r.SGST, '']);
            });
            rows.push(['', '', 'Total',
                salesRateWise.reduce((s, r) => s + (parseFloat(r.Taxable) || 0), 0),
                salesRateWise.reduce((s, r) => s + (parseFloat(r.IGST) || 0), 0),
                salesRateWise.reduce((s, r) => s + (parseFloat(r.CGST) || 0), 0),
                salesRateWise.reduce((s, r) => s + (parseFloat(r.SGST) || 0), 0), '']);
        }

        // Purchase Rate Wise
        if (purchaseRateWise.length > 0) {
            rows.push(['', '', '', '', '', '', '', '']);
            rows.push(['', '', 'Purchase - GST Rate Wise Breakup', '', '', '', '', '']);
            rows.push(['', '', 'GST Rate %', 'Taxable', 'IGST', 'CGST', 'SGST', '']);
            purchaseRateWise.forEach(r => {
                rows.push(['', '', `${r.GSTRate}%`, r.Taxable, r.IGST, r.CGST, r.SGST, '']);
            });
            rows.push(['', '', 'Total',
                purchaseRateWise.reduce((s, r) => s + (parseFloat(r.Taxable) || 0), 0),
                purchaseRateWise.reduce((s, r) => s + (parseFloat(r.IGST) || 0), 0),
                purchaseRateWise.reduce((s, r) => s + (parseFloat(r.CGST) || 0), 0),
                purchaseRateWise.reduce((s, r) => s + (parseFloat(r.SGST) || 0), 0), '']);
        }

        // Summary
        rows.push(['', '', '', '', '', '', '', '']);
        rows.push(['', '', 'Summary - Total GST Liability', '', '', '', '', '']);
        rows.push(['', '', 'Tax', 'Output', 'ITC', 'Net Payable', '', '']);
        rows.push(['', '', 'IGST', summary.OutputIGST, summary.ITCIGST, summary.NetIGSTPayable, '', '']);
        rows.push(['', '', 'CGST', summary.OutputCGST, summary.ITCCGST, summary.NetCGSTPayable, '', '']);
        rows.push(['', '', 'SGST', summary.OutputSGST, summary.ITCSGST, summary.NetSGSTPayable, '', '']);
        rows.push(['', '', 'Total GST Payable',
            (parseFloat(summary.OutputCGST)||0)+(parseFloat(summary.OutputSGST)||0)+(parseFloat(summary.OutputIGST)||0),
            (parseFloat(summary.ITCCGST)||0)+(parseFloat(summary.ITCSGST)||0)+(parseFloat(summary.ITCIGST)||0),
            summary.TotalGSTPayable, '', '']);

        await exportStyledExcel({
            reportTitle: 'GSTR 3B FORMAT REPORT', sheetName: 'GSTR3BFormat',
            fileName: `GSTR3B_Format_${fromDate}_${toDate}.xlsx`,
            companyName, username, fromDate, toDate, headers, rows,
            columnWidths: [10, 8, 44, 16, 14, 14, 14, 12],
        });
    };

    const th = { padding: '5px 8px', fontWeight: 600, fontSize: 10, color: '#fff', background: PRIMARY_MID, borderBottom: '1px solid #e2e8f0', textAlign: 'right', whiteSpace: 'nowrap' };
    const td = { padding: '4px 8px', fontSize: 11, borderBottom: '1px solid #f1f5f9', textAlign: 'right' };
    const tdL = { ...td, textAlign: 'left', fontWeight: 500, color: '#334155' };
    const sectionHeader = (bg, title) => (
        <div style={{ padding: '5px 10px', background: bg, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>{title}</div>
    );

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 8, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading GSTR 3B Format..." />
            <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '5px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 5, flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow><IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}><ArrowBack sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>GSTR 3B Format</span>
                    <div style={{ flex: 1 }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 26, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0 6px', fontSize: 11, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 26, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0 6px', fontSize: 11, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow><IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}><Refresh sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                    <Tooltip title="Export" arrow><IconButton onClick={handleExport} disabled={!data} size="small" sx={iconBtn}><GetApp sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                </div>

                {/* Body */}
                <div style={{ padding: '8px 10px' }}>
                    {!fetched && !loading && <div style={{ textAlign: 'center', padding: 28, color: '#94a3b8', fontSize: 12 }}>Select date range and click Refresh</div>}
                    {fetched && !data && <div style={{ textAlign: 'center', padding: 28, color: '#94a3b8', fontSize: 12 }}>No data found</div>}

                    {fetched && data && (
                        <>
                            {/* Summary Cards */}
                            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                                {[
                                    { label: 'Output Tax', value: `₹${fmtV((parseFloat(summary.OutputCGST)||0)+(parseFloat(summary.OutputSGST)||0)+(parseFloat(summary.OutputIGST)||0))}`, color: '#dc2626' },
                                    { label: 'ITC', value: `₹${fmtV((parseFloat(summary.ITCCGST)||0)+(parseFloat(summary.ITCSGST)||0)+(parseFloat(summary.ITCIGST)||0))}`, color: '#059669' },
                                    { label: 'Net Payable', value: `₹${fmtV(summary.TotalGSTPayable)}`, color: PRIMARY },
                                ].map(s => (
                                    <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 6, padding: '5px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 100 }}>
                                        <span style={{ fontSize: 9, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                        <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                                    </div>
                                ))}
                            </div>

                            {/* 3.1 - Outward Supplies */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                                {sectionHeader('#1e40af', '3.1 - Tax on Outward and Reverse Charge Inward Supplies')}
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={{ ...th, textAlign: 'left', width: '35%' }}>Nature of Supplies</th>
                                        <th style={th}>Taxable Value</th><th style={th}>IGST</th><th style={th}>CGST</th><th style={th}>SGST</th><th style={th}>CESS</th>
                                    </tr></thead>
                                    <tbody>
                                        {getSection('3.1').map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                                <td style={tdL}>{row.SlNo} {row.Particulars}</td>
                                                <td style={td}>{fmtV(row.TaxableValue)}</td>
                                                <td style={td}>{fmtV(row.IGST)}</td>
                                                <td style={td}>{fmtV(row.CGST)}</td>
                                                <td style={td}>{fmtV(row.SGST)}</td>
                                                <td style={td}>{fmtV(row.CESS)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* 4 - Eligible ITC */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                                {sectionHeader('#059669', '4 - Eligible ITC')}
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={{ ...th, textAlign: 'left', background: '#047857', width: '35%' }}>Details</th>
                                        <th style={{ ...th, background: '#047857' }}>Taxable Value</th>
                                        <th style={{ ...th, background: '#047857' }}>IGST</th>
                                        <th style={{ ...th, background: '#047857' }}>CGST</th>
                                        <th style={{ ...th, background: '#047857' }}>SGST</th>
                                        <th style={{ ...th, background: '#047857' }}>CESS</th>
                                    </tr></thead>
                                    <tbody>
                                        {getSection('4').map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f0fdf4', fontWeight: row.SlNo === '(C)' ? 700 : 400 }}>
                                                <td style={{ ...tdL, fontWeight: row.SlNo === '(C)' ? 700 : 500 }}>{row.SlNo} {row.Particulars}</td>
                                                <td style={td}>{fmtV(row.TaxableValue)}</td>
                                                <td style={{ ...td, color: '#059669' }}>{fmtV(row.IGST)}</td>
                                                <td style={{ ...td, color: '#059669' }}>{fmtV(row.CGST)}</td>
                                                <td style={{ ...td, color: '#059669' }}>{fmtV(row.SGST)}</td>
                                                <td style={td}>{fmtV(row.CESS)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* 5 - Exempt / Non-GST */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                                {sectionHeader('#7c3aed', '5 - Values of Exempt, Nil-Rated and Non-GST Inward Supplies')}
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={{ ...th, textAlign: 'left', background: '#6d28d9', width: '50%' }}>Nature of Supplies</th>
                                        <th style={{ ...th, background: '#6d28d9' }}>Taxable Value</th>
                                        <th style={{ ...th, background: '#6d28d9' }}>IGST</th>
                                    </tr></thead>
                                    <tbody>
                                        {getSection('5').map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f5f3ff' }}>
                                                <td style={tdL}>{row.SlNo} {row.Particulars}</td>
                                                <td style={td}>{fmtV(row.TaxableValue)}</td>
                                                <td style={td}>{fmtV(row.IGST)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* 6 - Payment of Tax */}
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                                {sectionHeader(PRIMARY, '6 - Payment of Tax')}
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={{ ...th, textAlign: 'left', width: '30%' }}>Description</th>
                                        <th style={th}>IGST</th><th style={th}>CGST</th><th style={th}>SGST</th><th style={th}>CESS</th>
                                    </tr></thead>
                                    <tbody>
                                        {getSection('6').map((row, i) => {
                                            const isNet = row.SlNo === 'NET';
                                            return (
                                                <tr key={i} style={{ background: isNet ? PRIMARY_LT : (i % 2 === 0 ? '#fff' : '#fafbfc') }}>
                                                    <td style={{ ...tdL, fontWeight: isNet ? 700 : 500, color: isNet ? PRIMARY : '#334155' }}>{row.SlNo} - {row.Particulars}</td>
                                                    <td style={{ ...td, fontWeight: isNet ? 800 : 400, color: isNet ? '#dc2626' : undefined }}>{fmtV(row.IGST)}</td>
                                                    <td style={{ ...td, fontWeight: isNet ? 800 : 400, color: isNet ? '#dc2626' : undefined }}>{fmtV(row.CGST)}</td>
                                                    <td style={{ ...td, fontWeight: isNet ? 800 : 400, color: isNet ? '#dc2626' : undefined }}>{fmtV(row.SGST)}</td>
                                                    <td style={{ ...td, fontWeight: isNet ? 800 : 400 }}>{fmtV(row.CESS)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Sales Rate Wise */}
                            {salesRateWise.length > 0 && (
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                                    {sectionHeader('#b45309', 'Sales - GST Rate Wise Breakup')}
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead><tr>
                                            <th style={{ ...th, textAlign: 'left', background: '#92400e' }}>GST Rate %</th>
                                            <th style={{ ...th, background: '#92400e' }}>Taxable</th>
                                            <th style={{ ...th, background: '#92400e' }}>IGST</th>
                                            <th style={{ ...th, background: '#92400e' }}>CGST</th>
                                            <th style={{ ...th, background: '#92400e' }}>SGST</th>
                                        </tr></thead>
                                        <tbody>
                                            {salesRateWise.map((row, i) => (
                                                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fffbeb' }}>
                                                    <td style={{ ...tdL, fontWeight: 600 }}>{row.GSTRate}%</td>
                                                    <td style={td}>{fmtV(row.Taxable)}</td>
                                                    <td style={td}>{fmtV(row.IGST)}</td>
                                                    <td style={td}>{fmtV(row.CGST)}</td>
                                                    <td style={td}>{fmtV(row.SGST)}</td>
                                                </tr>
                                            ))}
                                            <tr style={{ background: '#fef3c7' }}>
                                                <td style={{ ...tdL, fontWeight: 700, color: '#92400e' }}>Total</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV(salesRateWise.reduce((s, r) => s + (parseFloat(r.Taxable) || 0), 0))}</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV(salesRateWise.reduce((s, r) => s + (parseFloat(r.IGST) || 0), 0))}</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV(salesRateWise.reduce((s, r) => s + (parseFloat(r.CGST) || 0), 0))}</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV(salesRateWise.reduce((s, r) => s + (parseFloat(r.SGST) || 0), 0))}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Purchase Rate Wise */}
                            {purchaseRateWise.length > 0 && (
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                                    {sectionHeader('#0f766e', 'Purchase - GST Rate Wise Breakup')}
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead><tr>
                                            <th style={{ ...th, textAlign: 'left', background: '#115e59' }}>GST Rate %</th>
                                            <th style={{ ...th, background: '#115e59' }}>Taxable</th>
                                            <th style={{ ...th, background: '#115e59' }}>IGST</th>
                                            <th style={{ ...th, background: '#115e59' }}>CGST</th>
                                            <th style={{ ...th, background: '#115e59' }}>SGST</th>
                                        </tr></thead>
                                        <tbody>
                                            {purchaseRateWise.map((row, i) => (
                                                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f0fdfa' }}>
                                                    <td style={{ ...tdL, fontWeight: 600 }}>{row.GSTRate}%</td>
                                                    <td style={td}>{fmtV(row.Taxable)}</td>
                                                    <td style={td}>{fmtV(row.IGST)}</td>
                                                    <td style={td}>{fmtV(row.CGST)}</td>
                                                    <td style={td}>{fmtV(row.SGST)}</td>
                                                </tr>
                                            ))}
                                            <tr style={{ background: '#ccfbf1' }}>
                                                <td style={{ ...tdL, fontWeight: 700, color: '#115e59' }}>Total</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV(purchaseRateWise.reduce((s, r) => s + (parseFloat(r.Taxable) || 0), 0))}</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV(purchaseRateWise.reduce((s, r) => s + (parseFloat(r.IGST) || 0), 0))}</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV(purchaseRateWise.reduce((s, r) => s + (parseFloat(r.CGST) || 0), 0))}</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV(purchaseRateWise.reduce((s, r) => s + (parseFloat(r.SGST) || 0), 0))}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Summary Total */}
                            {summary && (
                                <div style={{ border: '2px solid #1c2266', borderRadius: 6, overflow: 'hidden', background: PRIMARY_LT }}>
                                    {sectionHeader(PRIMARY, 'Summary - Total GST Liability')}
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead><tr>
                                            <th style={{ ...th, textAlign: 'left' }}>Tax</th>
                                            <th style={th}>Output</th><th style={th}>ITC</th><th style={th}>Net Payable</th>
                                        </tr></thead>
                                        <tbody>
                                            <tr><td style={tdL}>IGST</td><td style={td}>{fmtV(summary.OutputIGST)}</td><td style={{ ...td, color: '#059669' }}>{fmtV(summary.ITCIGST)}</td><td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{fmtV(summary.NetIGSTPayable)}</td></tr>
                                            <tr style={{ background: '#fff' }}><td style={tdL}>CGST</td><td style={td}>{fmtV(summary.OutputCGST)}</td><td style={{ ...td, color: '#059669' }}>{fmtV(summary.ITCCGST)}</td><td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{fmtV(summary.NetCGSTPayable)}</td></tr>
                                            <tr><td style={tdL}>SGST</td><td style={td}>{fmtV(summary.OutputSGST)}</td><td style={{ ...td, color: '#059669' }}>{fmtV(summary.ITCSGST)}</td><td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{fmtV(summary.NetSGSTPayable)}</td></tr>
                                            <tr style={{ background: '#e0e7ff' }}>
                                                <td style={{ ...tdL, fontWeight: 700, color: PRIMARY, fontSize: 12 }}>Total GST Payable</td>
                                                <td style={{ ...td, fontWeight: 700 }}>{fmtV((parseFloat(summary.OutputCGST)||0)+(parseFloat(summary.OutputSGST)||0)+(parseFloat(summary.OutputIGST)||0))}</td>
                                                <td style={{ ...td, fontWeight: 700, color: '#059669' }}>{fmtV((parseFloat(summary.ITCCGST)||0)+(parseFloat(summary.ITCSGST)||0)+(parseFloat(summary.ITCIGST)||0))}</td>
                                                <td style={{ ...td, fontWeight: 800, color: '#dc2626', fontSize: 12 }}>₹{fmtV(summary.TotalGSTPayable)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GSTR3BFormatReport;
